type ServiceConsent = Record<string, boolean>;

type ConsentWindow = Window & {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    dotsandlinesConsent?: ServiceConsent;
    __dalConsentDefaultsSet?: boolean;
};

export type KlaroConsentOptions = {
    consents: ServiceConsent;
    confirmed: boolean;
};

// Runs before GTM, independently of the asynchronous Klaro download.
export const consentDefaultsScript = `
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () {
        window.dataLayer.push(arguments);
    };
    if (!window.__dalConsentDefaultsSet) {
        window.__dalConsentDefaultsSet = true;
        window.gtag('consent', 'default', {
            analytics_storage: 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            wait_for_update: 500
        });
        window.gtag('set', 'ads_data_redaction', true);
    }
`;

const serviceEvents = {
    'google-analytics': 'google-analytics',
    'google-ads': 'google-ads',
    'meta-pixel': 'meta-pixel',
    'google-double-click': 'doubleclick',
    linkedin: 'linkedin',
} as const;

// Klaro 0.7.21 supplies the complete applied choice to this callback.
// Keep the callback only on the required GTM service, not on every vendor.
export function syncKlaroConsent(options: KlaroConsentOptions) {
    if (typeof window === 'undefined') return;

    const trackingWindow = window as ConsentWindow;
    trackingWindow.dataLayer = trackingWindow.dataLayer || [];
    trackingWindow.gtag = trackingWindow.gtag || function () {
        trackingWindow.dataLayer!.push(arguments);
    };

    const previous = trackingWindow.dotsandlinesConsent;
    const services: ServiceConsent = {};
    for (const service of Object.keys(serviceEvents)) {
        // Unsaved selections must never be treated as applied permission.
        services[service] = options.confirmed === true && options.consents[service] === true;
    }

    if (previous && Object.keys(services).every(service => previous[service] === services[service])) {
        return;
    }

    // Expose the complete applied state before notifying dependent tags.
    trackingWindow.dotsandlinesConsent = services;
    const googleConsent = {
        analytics_storage: services['google-analytics'] ? 'granted' : 'denied',
        ad_storage: services['google-ads'] ? 'granted' : 'denied',
        ad_user_data: services['google-ads'] ? 'granted' : 'denied',
        ad_personalization: services['google-ads'] ? 'granted' : 'denied',
    };
    trackingWindow.gtag('consent', 'update', googleConsent);

    // Explicit state for the GTM consent template and vendor event guards.
    // Queuing gtag updates alone does not replace GTM's updateConsentState API.
    const consentServices = {
        google_analytics: services['google-analytics'],
        google_ads: services['google-ads'],
        meta_pixel: services['meta-pixel'],
        doubleclick: services['google-double-click'],
        linkedin: services.linkedin,
    };
    trackingWindow.dataLayer.push({
        event: 'klaro-consent-updated',
        consent_services: consentServices,
        consent_google: googleConsent,
    });

    for (const [service, eventName] of Object.entries(serviceEvents)) {
        if (!previous || previous[service] !== services[service]) {
            trackingWindow.dataLayer.push({
                event: `klaro-${eventName}-${services[service] ? 'accepted' : 'declined'}`,
                consent_services: consentServices,
            });
        }
    }
}
